import notaApi from '../config/notaApi';
import { API_ENDPOINTS } from '../constants/apiEndpoints';

const authService = {
  login: async (email, password) => {
    try {
      const response = await notaApi.post(API_ENDPOINTS.LOGIN, {
        email,
        password,
      });

      if (response.data.success) {
        const { token, refreshToken, user } = response.data.data;
        
        localStorage.setItem('notaToken', token);
        localStorage.setItem('notaRefreshToken', refreshToken);
        localStorage.setItem('notaUser', JSON.stringify(user));

        return response.data;
      }

      throw new Error(response.data.message || 'Login failed');
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  register: async (userData) => {
    try {
      const response = await notaApi.post(API_ENDPOINTS.REGISTER, userData);

      if (response.data.success) {
        // Don't store tokens yet - user needs to verify email first
        return response.data;
      }

      throw new Error(response.data.message || 'Registration failed');
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  verifyEmail: async (email, otp) => {
    try {
      const response = await notaApi.post(API_ENDPOINTS.VERIFY_EMAIL, {
        email,
        otp,
      });

      if (response.data.success) {
        const { token, refreshToken, user } = response.data.data;
        
        localStorage.setItem('notaToken', token);
        localStorage.setItem('notaRefreshToken', refreshToken);
        localStorage.setItem('notaUser', JSON.stringify(user));

        return response.data;
      }

      throw new Error(response.data.message || 'Email verification failed');
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  resendOTP: async (email) => {
    try {
      const response = await notaApi.post(API_ENDPOINTS.RESEND_OTP, {
        email,
      });

      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  logout: async () => {
    try {
      await notaApi.post(API_ENDPOINTS.LOGOUT);
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('notaToken');
      localStorage.removeItem('notaRefreshToken');
      localStorage.removeItem('notaUser');
    }
  },

  getCurrentUser: async () => {
    try {
      const response = await notaApi.get(API_ENDPOINTS.ME);
      
      if (response.data.success) {
        const user = response.data.data;
        localStorage.setItem('notaUser', JSON.stringify(user));
        return user;
      }

      throw new Error('Failed to get current user');
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  refreshToken: async (refreshToken) => {
    try {
      const response = await notaApi.post(API_ENDPOINTS.REFRESH, {
        refreshToken,
      });

      if (response.data.success) {
        const { token } = response.data.data;
        localStorage.setItem('notaToken', token);
        return token;
      }

      throw new Error('Token refresh failed');
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  forgotPassword: async (email, source = 'main') => {
    try {
      const response = await notaApi.post(API_ENDPOINTS.FORGOT_PASSWORD, {
        email,
        source, // 'main' or 'app' to determine which frontend URL to use
      });

      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  resetPassword: async (token, password) => {
    try {
      const response = await notaApi.post(API_ENDPOINTS.RESET_PASSWORD, {
        token,
        password,
      });

      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  updateProfile: async (profileData) => {
    try {
      const response = await notaApi.put(API_ENDPOINTS.UPDATE_PROFILE, profileData);

      if (response.data.success) {
        const user = response.data.data;
        localStorage.setItem('notaUser', JSON.stringify(user));
        return response.data;
      }

      throw new Error('Profile update failed');
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  changePassword: async (currentPassword, newPassword) => {
    try {
      const response = await notaApi.put(API_ENDPOINTS.CHANGE_PASSWORD, {
        currentPassword,
        newPassword,
      });

      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  getStoredUser: () => {
    const userStr = localStorage.getItem('notaUser');
    return userStr ? JSON.parse(userStr) : null;
  },

  getStoredToken: () => {
    return localStorage.getItem('notaToken');
  },

  isAuthenticated: () => {
    return !!localStorage.getItem('notaToken');
  },
};

export default authService;
